import { action, computed, observable } from 'mobx'
import { ROUTES } from '@/constants/router.js'

export default class RouterStore {
  rootStore = null

  #router = null
  #events = []

  @observable accessor isInitialized = false
  @observable accessor routes = {}
  @observable accessor currentRoute = ''
  @observable accessor url = ''
  @observable accessor href = ''
  @observable accessor routeData = {}
  @observable accessor path = ''
  @observable accessor params = {}
  @observable accessor search = {}
  @observable accessor hash = ''
  @observable accessor is404 = false

  constructor(rootStore) {
    this.rootStore = rootStore
    this.rootStore.registerDeconstructor(this.#_deconstructor)
  }

  @action
  #_deconstructor = () => {
    this.#events.forEach(x => x())
    this.#events = []
    this.#router = null
    this.routes = {}
  }

  @action
  init = router => {
    if (this.#router) return

    this.#router = router
    this.#setRoutes()
    this.#updateState()
    this.#events.push(this.#router.subscribe('onResolved', this.#updateState))

    this.isInitialized = true
    console.log('[Router] Ready')
  }

  get auth() {
    return this.rootStore.authStore
  }

  @computed
  get isFavoritesPage() {
    return this.currentRoute === this.routes.favorites
  }

  @computed
  get isHomePage() {
    return this.currentRoute === this.routes.index
  }

  @computed
  get isLivestreamPage() {
    return this.currentRoute === this.routes.livestream
  }

  @computed
  get isMeetingPage() {
    return this.currentRoute === this.routes.meeting
  }

  @computed
  get isOnramperPage() {
    return this.currentRoute === this.routes.onramper
  }

  @computed
  get isPostPage() {
    return this.currentRoute === this.routes.post
  }

  @computed
  get isReportsPage() {
    return this.currentRoute === this.routes.reports
  }

  @computed
  get isStorePage() {
    return this.currentRoute === this.routes.store
  }

  @computed
  get isCurrentUserStorePage() {
    return this.isStorePage && this.params.handle === this.auth.user?.handle
  }

  buildUrl = (
    opts = {
      from: '',
      hash: '',
      mask: null,
      params: {},
      search: {},
      state: false,
      to: null,
    }
  ) => `${import.meta.env.VITE_APP_FRONTEND_URL}${this.buildLocation(opts).href}`

  buildLocation = (
    opts = {
      from: '',
      hash: '',
      mask: null,
      params: {},
      search: {},
      state: false,
      to: null,
    }
  ) => this.#router.buildLocation(opts)

  navigate = async (
    to,
    opts = {
      hash: '',
      hashScrollIntoView: true,
      ignoreBlocker: false,
      params: {},
      reloadDocument: false,
      replace: false,
      resetScroll: true,
      search: {},
      viewTransition: true,
    }
  ) => {
    if (to) {
      if (to.startsWith('/')) return this.#router.navigate({ to, ...opts })

      opts.href = to
    }

    return this.#router.navigate(opts)
  }

  reload = () => {
    window.navigation.reload()
  }

  @action
  #updateState = () => {
    this.href = this.#router.latestLocation.href
    this.path = this.#router.latestLocation.pathname
    this.search = this.#router.latestLocation.search
    this.hash = this.#router.latestLocation.hash.split('=')[0] // FIXME: tanstack router issue? keeps adding = to hash

    const matches = this.#router.state.matches
    const matchedRoute = matches.length > 0 ? matches[matches.length - 1] : null

    this.params = matchedRoute?.params ?? {}
    this.currentRoute = matchedRoute?.fullPath ?? ''
    this.routeData = matchedRoute?.staticData ?? {}
    this.is404 = !matchedRoute

    this.url = `${import.meta.env.VITE_APP_FRONTEND_URL}${this.path}`
    if (this.url.endsWith('/')) this.url = this.url.slice(0, -1)
  }

  @action
  #setRoutes = () => {
    ROUTES.forEach(([id, route]) => {
      this.routes[id] = route.fullPath
    })
  }
}
